import ePub from "epubjs"
import { EpubReadAhead } from "../epub_read_ahead.js"
import { locatorForRange, rangeForLocator } from "../epub_locator.js"
import {
  DEFAULT_HIGHLIGHT_COLOR,
  highlightColor,
  readHighlightColor,
  selectColorButtons,
  validHighlightColor
} from "../highlight_colors.js"
import {
  closeNoteDialog,
  openNoteDialog,
  removeBookmarkRecords,
  removeHighlightRecord,
  renderBookmarkList,
  renderHighlightList,
  shareAnnotationLocation,
  saveHighlightNote,
  updateHighlightColor
} from "../reader_annotations.js"
import { requestJson } from "../reader_api.js"
import { ReaderSearch } from "../reader_search.js"
import { ReaderSidebar } from "../reader_sidebar.js"
import { startReaderErrorTracking } from "../reader_error_tracking.js"
import { readerFindShortcut, readerPageShortcut } from "../reader_keyboard_shortcut.js"
import { consumeReaderLocation } from "../reader_location.js"
import { normalizeSearchText } from "../search_text.js"
import ReaderController from "./reader_controller.js"

export default class extends ReaderController {
  static outlets = ["fast-flip"]

  static targets = [
    "viewport", "sidebar", "toc", "highlights", "bookmarks",
    "chapter", "selectionDialog", "selectionQuote", "selectionStatus", "translationButton", "translationAvailability",
    "translationDialog", "translationOriginal", "translationLanguage", "translationStatus", "translationResult",
    "translationText", "translationCopyStatus", "translationSubmit", "noteDialog", "noteInput",
    "noteTitle", "panel", "panelButton", "searchInput", "searchStatus", "searchResults",
    "sidebarToggle", "bookmarkButton", "selectionColor", "noteColor", "colorStatus", "highlightFilter", "highlightStatus", "scaleValue", "signInLink"
  ]

  static values = {
    contentUrl: String,
    progressUrl: String,
    highlightsUrl: String,
    bookmarksUrl: String,
    translationUrl: String,
    searchResultsUrl: String,
    preferencesUrl: String,
    readOnly: { type: Boolean, default: false },
    startLocation: { type: Object, default: {} },
    sharedLocation: { type: Object, default: {} },
    theme: { type: String, default: "light" },
    fontScale: { type: Number, default: 100 },
    initialSearch: { type: Object, default: {} },
    labels: Object,
    highlightColors: Object,
    appsignalApiKey: String
  }

  async connect() {
    this.errorTracker = startReaderErrorTracking({
      key: this.appsignalApiKeyValue,
      action: "Reader#epub"
    })
    this.currentLocation = null
    this.highlightRecords = []
    this.highlightFilterColor = ""
    this.pendingHighlightColor = this.readOnlyValue ? DEFAULT_HIGHLIGHT_COLOR : readHighlightColor(this.highlightColorsValue)
    this.bookmarkRecords = []
    this.bookmarksLoaded = false
    this.bookmarkTogglePending = false
    this.pendingHighlightCfis = new Set()
    this.progressTimer = null
    this.highlightStatusTimer = null
    this.touchStartX = null
    this.pendingSelection = null
    this.selectionPointerType = null
    this.selectionPointerPressed = false
    this.toolbarSelectionCaptured = false
    this.setTranslationOffline(globalThis.navigator?.onLine === false)
    this.searchPreviewCfis = new Set()
    this.currentSearchCfi = null
    this.currentSection = null
    this.readerSidebar = new ReaderSidebar({
      element: this.element,
      sidebar: this.sidebarTarget,
      toggle: this.sidebarToggleTarget,
      panels: this.panelTargets,
      buttons: this.panelButtonTargets,
      onSearchVisibilityChanged: (visible) => visible ? this.renderSearchResultHighlights() : this.removeSearchResultHighlights(),
      onDesktopLayout: () => new Promise((resolve) => window.requestAnimationFrame(() => {
        this.rendition?.resize(this.viewportTarget.clientWidth, this.viewportTarget.clientHeight)
        resolve()
      }))
    })
    this.readerSearch = new ReaderSearch({
      url: this.searchResultsUrlValue,
      input: this.searchInputTarget,
      status: this.searchStatusTarget,
      results: this.searchResultsTarget,
      labels: this.labelsValue,
      request: requestJson,
      titleFor: (result) => result.section_title || this.labelsValue.unknownChapter,
      openResult: (result, options) => this.openSearchResult(result, options),
      resetHighlights: () => this.removeSearchResultHighlights(),
      renderHighlights: () => this.renderSearchResultHighlights()
    })

    try {
      this.book = ePub(this.contentUrlValue, { openAs: "opf", replacements: "none" })
      this.readAhead = new EpubReadAhead({ book: this.book })
      this.rendition = this.book.renderTo(this.viewportTarget, {
        width: "100%",
        height: "100%",
        flow: "paginated",
        manager: "default",
        allowScriptedContent: false
      })

      this.registerThemes()
      this.registerReaderEvents()
      await this.book.ready
      this.renderTableOfContents()
      this.applyPreferences()

      const sharedCfi = await this.sharedCfi()
      await this.rendition.display(sharedCfi || this.startLocationValue.cfi || undefined)
      consumeReaderLocation()

      // Fetch secondary data only after the saved location is visible.
      if (!this.readOnlyValue) await Promise.all([this.loadHighlights(), this.loadBookmarks()])
      await this.openInitialSearch()
    } catch (error) {
      this.viewportTarget.innerHTML = `<p class="reader-error">${this.escape(this.labelsValue.loadError)}</p>`
      this.errorTracker.reportError(error)
      console.error("Unable to initialize EPUB reader", error)
    }
  }

  disconnect() {
    window.clearTimeout(this.progressTimer)
    window.clearTimeout(this.highlightStatusTimer)
    this.readAhead?.disconnect()
    this.readerSearch.disconnect()
    this.errorTracker?.disconnect()
    this.book?.destroy()
  }

  async sharedCfi() {
    const shared = this.sharedLocationValue
    if (shared.location?.cfi) return shared.location.cfi
    if (!shared.semantic_locator) return null

    try {
      const locator = shared.semantic_locator
      const section = this.book.spine.get(locator.source?.href)
      if (!section) throw new Error("Shared EPUB section is unavailable")

      await section.load(this.book.load.bind(this.book))
      const range = rangeForLocator(section.document, locator)
      if (!range) throw new Error("Shared EPUB range is unavailable")

      return section.cfiFromRange(range)
    } catch (error) {
      console.error("Unable to open shared EPUB location", error)
      this.showHighlightStatus(this.labelsValue.sharedLocationError)
      return null
    }
  }

  previous() {
    this.rendition?.prev()
  }

  next() {
    this.rendition?.next()
  }

  toggleSidebar() {
    this.readerSidebar.toggleOpen()
  }

  async openSearchPanel() {
    this.readerSidebar.activate("search")
    await this.readerSidebar.setOpen(true)
    this.searchInputTarget.focus()
  }

  handleKeyboardShortcut(event) {
    if (readerFindShortcut(event)) {
      event.preventDefault()
      this.openSearchPanel()
      return
    }

    if (!readerPageShortcut(event)) return
    event.preventDefault()
    event.key === "ArrowLeft" ? this.previous() : this.next()
  }

  setSidebarOpen(open) {
    return this.readerSidebar.setOpen(open)
  }

  showPanel(event) {
    const selected = event.currentTarget.dataset.panelName
    if (this.readOnlyValue && ["highlights", "bookmarks"].includes(selected)) return

    this.activatePanel(selected)
  }

  activatePanel(selected) {
    this.readerSidebar.activate(selected)
  }

  async searchBook(event) {
    event.preventDefault()
    await this.readerSearch.load(this.searchInputTarget.value)
  }

  async toggleTheme() {
    this.themeValue = this.themeValue === "light" ? "dark" : "light"
    this.applyPreferences()
    await this.savePreferences()
  }

  async increaseFont() {
    this.fontScaleValue = Math.min(160, this.fontScaleValue + 10)
    this.applyPreferences()
    await this.savePreferences()
  }

  async decreaseFont() {
    this.fontScaleValue = Math.max(80, this.fontScaleValue - 10)
    this.applyPreferences()
    await this.savePreferences()
  }

  fontScaleValueChanged(value) {
    this.scaleValueTarget.textContent = `${value}%`
  }

  async toggleBookmark() {
    if (this.readOnlyValue) return
    if (!this.currentLocation || !this.bookmarksLoaded || this.bookmarkTogglePending) return

    this.bookmarkTogglePending = true
    this.bookmarkButtonTarget.disabled = true
    try {
      const bookmarks = this.visibleBookmarks()
      if (bookmarks.length > 0) {
        await this.removeBookmarks(bookmarks)
      } else {
        await this.addBookmark()
      }
    } finally {
      this.bookmarkTogglePending = false
      this.bookmarkButtonTarget.disabled = !this.bookmarksLoaded
      this.syncBookmarkButton()
    }
  }

  async addBookmark() {
    if (!this.currentLocation) return

    const locator = await this.locatorForCfi(this.currentLocation.start.cfi)
    if (!locator) return
    const { percentage } = this.readerPosition(this.currentLocation)

    const record = await requestJson(this.bookmarksUrlValue, {
      method: "POST",
      body: {
        bookmark: {
          location: { format: "epub", cfi: this.currentLocation.start.cfi },
          current_section: this.chapterTarget.textContent,
          label: this.chapterTarget.textContent,
          semantic_locator: locator,
          progress_percentage: percentage
        }
      }
    })
    if (!record) return

    this.bookmarkRecords.push(record)
    this.renderBookmarks()
    this.syncBookmarkButton()
  }

  openFastFlipLocation(cfi) {
    return this.rendition?.display(cfi)
  }

  fastFlipOutletConnected(outlet) {
    if (this.currentLocation) outlet.sync(this.readerPosition(this.currentLocation))
  }

  async saveNote(event) {
    event.preventDefault()
    if (this.readOnlyValue) return
    const highlight = this.pendingHighlight
    const body = this.noteInputTarget.value.trim()
    if (!highlight) return

    if (await saveHighlightNote({ highlight, body, request: requestJson })) {
      this.renderHighlights()
      this.closeNote()
    }
  }

  closeNote() {
    closeNoteDialog({ dialog: this.noteDialogTarget, input: this.noteInputTarget })
    this.pendingHighlight = null
  }

  async removePendingHighlight() {
    if (this.readOnlyValue) return
    if (await this.removeHighlight(this.pendingHighlight)) this.closeNote()
  }

  registerThemes() {
    this.rendition.themes.register("light", {
      body: { color: "#20233a", background: "#ffffff" },
      a: { color: "#040e85" },
      "::selection": { background: "rgba(198, 156, 108, 0.35)" },
      ".epubjs-hl": { fill: "#c69c6c", "fill-opacity": "0.38", "mix-blend-mode": "multiply" }
    })
    this.rendition.themes.register("dark", {
      body: { color: "#f1f2f8", background: "#171827" },
      a: { color: "#b8bdf8" },
      "::selection": { background: "rgba(198, 156, 108, 0.5)" },
      ".epubjs-hl": { fill: "#c69c6c", "fill-opacity": "0.55", "mix-blend-mode": "screen" }
    })
  }

  applyPreferences() {
    this.rendition?.themes.select(this.themeValue)
    this.rendition?.themes.fontSize(`${this.fontScaleValue}%`)
    this.element.dataset.readerTheme = this.themeValue
  }

  registerReaderEvents() {
    this.rendition.on("relocated", (location) => this.onRelocated(location))
    this.rendition.on("rendered", (section) => this.onRendered(section))
    this.rendition.on("selected", (cfiRange, contents) => this.onSelected(cfiRange, contents))
    this.rendition.hooks.content.register((contents) => this.prepareContents(contents))
  }

  onRendered(section) {
    this.currentSection = section
    this.renderSearchResultHighlights(section)
    this.readAhead.rendered(section)
  }

  // Retain these accessors for system diagnostics without leaking ownership of
  // the read-ahead lifecycle back into the Stimulus controller.
  get readAheadSection() {
    return this.readAhead?.section || null
  }

  get readAheadTask() {
    return this.readAhead?.task || null
  }

  readAheadAllowed() {
    return this.readAhead?.allowed() || false
  }

  refreshReadAhead() {
    this.readAhead?.refresh()
  }

  prepareContents(contents) {
    contents.document.querySelectorAll("a[href]").forEach((link) => {
      const href = link.getAttribute("href")
      if (/^https?:\/\//i.test(href)) {
        link.setAttribute("target", "_blank")
        link.setAttribute("rel", "noopener noreferrer")
      }
    })

    contents.document.addEventListener("keydown", (event) => {
      this.dispatch("reading-activity")
      this.handleKeyboardShortcut(event)
    })
    contents.document.addEventListener("pointerdown", (event) => {
      this.dispatch("reading-activity")
      this.selectionPointerType = event.pointerType
      this.selectionPointerPressed = true
    })
    contents.document.addEventListener("pointerup", () => {
      this.selectionPointerPressed = false
      if (!this.readOnlyValue && this.selectionPointerType === "mouse") this.openSelectionDialog()
    })
    contents.document.addEventListener("pointercancel", () => {
      this.selectionPointerPressed = false
    })
    contents.document.addEventListener("touchstart", (event) => {
      this.dispatch("reading-activity")
      this.touchStartX = event.changedTouches[0]?.screenX
    }, { passive: true })
    contents.document.addEventListener("touchend", (event) => {
      const endX = event.changedTouches[0]?.screenX
      if (this.touchStartX == null || endX == null) return
      const distance = endX - this.touchStartX
      this.touchStartX = null
      if (Math.abs(distance) > 60) distance > 0 ? this.previous() : this.next()
    }, { passive: true })
  }

  onRelocated(location) {
    if (!this.readOnlyValue) this.dismissSelection()
    this.currentLocation = location
    this.updateSignInLocation(location.start.cfi)
    this.syncBookmarkButton()
    const { chapter, percentage } = this.readerPosition(location)
    this.chapterTarget.textContent = chapter
    if (this.hasFastFlipOutlet) this.fastFlipOutlet.sync({ chapter, percentage })

    if (!this.readOnlyValue) {
      window.clearTimeout(this.progressTimer)
      this.progressTimer = window.setTimeout(() => this.saveProgress(location.start.cfi, chapter, percentage), 700)
    }
  }

  updateSignInLocation(cfi) {
    if (!this.readOnlyValue || !this.hasSignInLinkTarget || !cfi) return

    const url = new URL(this.signInLinkTarget.href)
    url.searchParams.set("location[format]", "epub")
    url.searchParams.set("location[cfi]", cfi)
    this.signInLinkTarget.href = url.toString()
  }

  readerPosition(location) {
    return {
      chapter: this.book.navigation.get(location.start.href)?.label?.trim() || this.labelsValue.unknownChapter,
      percentage: this.approximateProgress(location)
    }
  }

  approximateProgress(location) {
    if (location.atEnd) return 100

    const sections = this.book.spine.spineItems.filter((section) => section.linear)
    const sectionPosition = sections.findIndex((section) => section.index === location.start.index)
    if (sectionPosition < 0 || sections.length === 0) return 0

    const page = location.start.displayed?.page || 1
    const pages = location.start.displayed?.total || 1
    const sectionProgress = Math.max(0, Math.min(1, (page - 1) / pages))

    return Math.round(((sectionPosition + sectionProgress) / sections.length) * 10000) / 100
  }

  async onSelected(cfiRange, contents) {
    if (this.readOnlyValue) return

    let selection = this.selectionFromContents(contents, cfiRange)
    if (!selection) {
      const range = await this.book.getRange(cfiRange)
      const selectedText = range?.toString().trim()
      const section = this.book.spine.get(new ePub.CFI(cfiRange).spinePos)
      const locator = range && section && locatorForRange(section, range, selectedText)
      if (selectedText && locator) selection = { cfiRange, selectedText, locator, contents }
    }

    if (!this.setPendingSelection(selection)) return
    if (this.selectionPointerPressed) return
    if (this.selectionPointerType && this.selectionPointerType !== "mouse") return

    this.openSelectionDialog()
  }

  selectionFromContents(contents, cfiRange = null) {
    const selection = contents.window.getSelection()
    const range = selection?.rangeCount > 0 ? selection.getRangeAt(0) : null
    const selectedText = selection?.toString().trim()
    if (!range || range.collapsed || !selectedText) return null

    const selectedCfi = cfiRange || contents.cfiFromRange(range)
    const section = this.book.spine.get(contents.sectionIndex) || this.currentSection
    const locator = section && locatorForRange(section, range, selectedText)
    if (!locator) return null
    return { cfiRange: selectedCfi, selectedText, locator, contents }
  }

  setPendingSelection(selection) {
    if (!selection) return false
    if (this.pendingHighlightCfis.has(selection.cfiRange)) return false
    if (this.highlightRecords.some((highlight) => highlight.location.cfi === selection.cfiRange)) return false

    this.pendingSelection = selection
    return true
  }

  captureToolbarSelection() {
    if (this.readOnlyValue) return
    if (this.toolbarSelectionCaptured) return

    const selection = this.rendition?.getContents()
      .map((contents) => this.selectionFromContents(contents))
      .find(Boolean)
    this.toolbarSelectionCaptured = this.setPendingSelection(selection)
  }

  openToolbarSelection() {
    if (this.readOnlyValue) return
    this.clearHighlightStatus()
    const hasSelectedText = this.rendition?.getContents()
      .some((contents) => Boolean(contents.window.getSelection()?.toString().trim()))
    if (!this.toolbarSelectionCaptured) this.captureToolbarSelection()
    this.toolbarSelectionCaptured = false
    if (!this.pendingSelection) {
      if (!hasSelectedText) this.showHighlightStatus()
      return
    }

    this.openSelectionDialog()
  }

  openSelectionDialog() {
    if (!this.pendingSelection || this.selectionDialogTarget.open) return

    this.pendingHighlightColor = readHighlightColor(this.highlightColorsValue)
    selectColorButtons(this.selectionColorTargets, this.pendingHighlightColor)
    this.selectionQuoteTarget.textContent = this.pendingSelection.selectedText
    this.clearBrowserSelection(this.pendingSelection.contents)
    this.selectionDialogTarget.showModal()
  }

  dismissSelection(event) {
    if (this.selectionClosePreservesPendingSelection(event)) return

    event?.preventDefault()
    if (this.hasSelectionDialogTarget && this.selectionDialogTarget.open) this.selectionDialogTarget.close()
    this.clearBrowserSelection(this.pendingSelection?.contents)
    this.selectionQuoteTarget.textContent = ""
    this.pendingSelection = null
    this.toolbarSelectionCaptured = false
  }

  clearBrowserSelection(contents) {
    contents?.window.getSelection()?.removeAllRanges()
  }

  async persistSelection(addNote) {
    if (this.readOnlyValue) return
    const selection = this.pendingSelection
    if (!selection) return

    const { cfiRange, selectedText, locator } = selection
    const existingHighlight = this.highlightRecords.find((highlight) => highlight.location.cfi === cfiRange)
    if (existingHighlight) {
      this.dismissSelection()
      if (addNote) this.openNote(existingHighlight)
      return
    }
    if (this.pendingHighlightCfis.has(cfiRange)) return

    this.pendingHighlightCfis.add(cfiRange)

    try {
      const highlight = await requestJson(this.highlightsUrlValue, {
        method: "POST",
        body: {
          highlight: {
            color: validHighlightColor(this.highlightColorsValue, this.pendingHighlightColor) ? this.pendingHighlightColor : DEFAULT_HIGHLIGHT_COLOR,
            location: { format: "epub", cfi: cfiRange },
            selected_text: selectedText,
            semantic_locator: locator
          }
        }
      })
      if (!highlight) return

      this.highlightRecords.push(highlight)
      this.applyHighlight(highlight)
      this.renderHighlights()
      this.dismissSelection()
      if (addNote) this.openNote(highlight)
    } finally {
      this.pendingHighlightCfis.delete(cfiRange)
    }
  }

  async saveProgress(cfi, chapter, percentage) {
    if (this.readOnlyValue) return
    const locator = await this.locatorForCfi(cfi)
    if (!locator) return
    await requestJson(this.progressUrlValue, {
      method: "PATCH",
      body: {
        reading: {
          location: { format: "epub", cfi },
          current_section: chapter,
          progress_percentage: percentage,
          semantic_locator: locator
        }
      }
    })
  }

  async locatorForCfi(cfi) {
    const range = await this.book.getRange(cfi)
    const spinePosition = new ePub.CFI(cfi).spinePos
    const section = this.book.spine.get(spinePosition)
    return range && section ? locatorForRange(section, range) : null
  }

  async savePreferences() {
    if (this.readOnlyValue) return
    await requestJson(this.preferencesUrlValue, {
      method: "PATCH",
      body: { user: { reader_theme: this.themeValue, reader_font_scale: this.fontScaleValue } }
    })
  }

  async loadHighlights() {
    if (this.readOnlyValue) return
    this.highlightRecords = await requestJson(this.highlightsUrlValue) || []
    this.highlightRecords.forEach((highlight) => this.applyHighlight(highlight))
    this.renderHighlights()
  }

  async loadBookmarks() {
    if (this.readOnlyValue) return
    const records = await requestJson(this.bookmarksUrlValue)
    if (!records) return

    this.bookmarkRecords = records
    this.bookmarksLoaded = true
    this.bookmarkButtonTarget.disabled = false
    this.renderBookmarks()
    this.syncBookmarkButton()
  }

  visibleBookmarks() {
    const start = this.currentLocation?.start?.cfi
    const end = this.currentLocation?.end?.cfi || start
    if (!start || !end) return []

    const comparator = new ePub.CFI()
    return this.bookmarkRecords.filter((bookmark) => {
      try {
        return comparator.compare(start, bookmark.location.cfi) <= 0 && comparator.compare(bookmark.location.cfi, end) <= 0
      } catch (_error) {
        return false
      }
    })
  }

  syncBookmarkButton() {
    const active = this.visibleBookmarks().length > 0
    this.bookmarkButtonTarget.classList.toggle("is-active", active)
    this.bookmarkButtonTarget.setAttribute("aria-pressed", String(active))
  }

  applyHighlight(highlight) {
    // EPUB.js keys overlays by CFI; merged notes can share that location.
    this.rendition.annotations.remove(highlight.location.cfi, "highlight")
    const color = highlightColor(this.highlightColorsValue, highlight.color)
    this.rendition.annotations.highlight(
      highlight.location.cfi,
      { highlightId: highlight.id },
      () => this.openNote(highlight),
      "epubjs-hl",
      { fill: color.hex, "fill-opacity": "0.4" }
    )
  }

  renderTableOfContents() {
    this.tocTarget.replaceChildren(...this.book.navigation.toc.map((item) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "reader-list-button"
      button.textContent = item.label.trim()
      button.addEventListener("click", async () => {
        await this.rendition.display(item.href)
        await this.closeSidebarAfterNavigation()
      })
      return button
    }))
  }

  async openInitialSearch() {
    const search = this.initialSearchValue
    if (!search?.query) return

    this.activatePanel("search")
    this.setSidebarOpen(true)
    this.searchInputTarget.value = search.query
    await this.readerSearch.load(search.query, search)
  }

  async openSearchResult(result, { closeSidebar = true } = {}) {
    try {
      const section = this.book.spine.get(result.source?.href)
      if (!section) throw new Error("Search result section is unavailable")

      await section.load(this.book.load.bind(this.book))
      const range = this.searchRange(section.document, result.block_index, result.match_offset, this.searchInputTarget.value)
      if (!range) throw new Error("Search result range is unavailable")

      const cfi = section.cfiFromRange(range)
      if (closeSidebar) await this.closeSidebarAfterNavigation()
      this.removeSearchResultHighlights()
      this.removeSearchHighlight()
      this.currentSearchCfi = cfi
      this.rendition.annotations.highlight(
        cfi,
        { search: true },
        null,
        "epubjs-search-result",
        { fill: this.highlightColorsValue.blue.hex, "fill-opacity": "0.4" }
      )
      await this.rendition.display(cfi)
    } catch (error) {
      console.error("Unable to open search result", error)
      this.searchStatusTarget.textContent = this.labelsValue.searchError
    }
  }

  searchRange(document, blockIndex, matchOffset, query) {
    const normalizedQuery = normalizeSearchText(query)
    return rangeForLocator(document, {
      block_index: blockIndex,
      start_offset: matchOffset,
      end_offset: matchOffset + normalizedQuery.length,
      quote: query
    })
  }

  removeSearchHighlight() {
    if (!this.currentSearchCfi) return

    const cfi = this.currentSearchCfi
    this.rendition.annotations.remove(cfi, "highlight")
    this.currentSearchCfi = null

    const persistedHighlight = this.highlightRecords.find((highlight) => highlight.location.cfi === cfi)
    if (persistedHighlight) this.applyHighlight(persistedHighlight)
  }

  renderSearchResultHighlights(section = this.currentSection) {
    this.removeSearchResultHighlights()
    if (!section || !this.searchResultsVisible()) return

    this.readerSearch.records.filter((result) => result.source?.href === section.href).forEach((result) => {
      const range = this.searchRange(section.document, result.block_index, result.match_offset, this.searchInputTarget.value)
      if (!range) return

      const cfi = section.cfiFromRange(range)
      if (cfi === this.currentSearchCfi) return

      this.rendition.annotations.highlight(
        cfi,
        { search: true },
        null,
        "epubjs-search-match",
        { fill: this.highlightColorsValue.blue.hex, "fill-opacity": "0.4" }
      )
      this.searchPreviewCfis.add(cfi)
    })
  }

  removeSearchResultHighlights() {
    this.searchPreviewCfis.forEach((cfi) => {
      this.rendition?.annotations.remove(cfi, "highlight")
      const persistedHighlight = this.highlightRecords.find((highlight) => highlight.location.cfi === cfi)
      if (persistedHighlight) this.applyHighlight(persistedHighlight)
    })
    this.searchPreviewCfis.clear()
  }

  searchResultsVisible() {
    return this.readerSidebar.searchVisible()
  }

  renderHighlights() {
    renderHighlightList({
      target: this.highlightsTarget,
      records: this.highlightRecords,
      filterColor: this.highlightFilterColor,
      palette: this.highlightColorsValue,
      labels: this.labelsValue,
      onNavigate: async (highlight) => {
        await this.rendition.display(highlight.location.cfi)
        await this.closeSidebarAfterNavigation()
      },
      onEdit: (highlight) => this.openNote(highlight),
      onShare: (highlight) => this.shareAnnotation(highlight),
      onRemove: (highlight) => this.removeHighlight(highlight)
    })
  }

  renderBookmarks() {
    renderBookmarkList({
      target: this.bookmarksTarget,
      records: this.bookmarkRecords,
      labels: this.labelsValue,
      labelFor: (bookmark) => bookmark.label || bookmark.current_section || this.labelsValue.bookmark,
      onNavigate: async (bookmark) => {
        await this.rendition.display(bookmark.location.cfi)
        await this.closeSidebarAfterNavigation()
      },
      onShare: (bookmark) => this.shareAnnotation(bookmark),
      onRemove: (bookmark) => this.removeBookmark(bookmark)
    })
  }

  shareAnnotation(record) {
    if (this.readOnlyValue) return
    return shareAnnotationLocation({ record, request: requestJson, clipboard: navigator.clipboard })
  }

  openNote(highlight) {
    if (this.readOnlyValue) return
    this.pendingHighlight = highlight
    openNoteDialog({
      highlight,
      dialog: this.noteDialogTarget,
      title: this.noteTitleTarget,
      input: this.noteInputTarget,
      status: this.colorStatusTarget,
      colorButtons: this.noteColorTargets
    })
  }

  async changeHighlightColor(event) {
    if (this.readOnlyValue) return
    const highlight = this.pendingHighlight
    const color = event.currentTarget.dataset.highlightColor
    await updateHighlightColor({
      highlight,
      color,
      palette: this.highlightColorsValue,
      buttons: this.noteColorTargets,
      status: this.colorStatusTarget,
      errorLabel: this.labelsValue.colorUpdateError,
      request: requestJson,
      onUpdated: (updatedHighlight) => {
        this.applyHighlight(updatedHighlight)
        this.renderHighlights()
      }
    })
  }

  async removeHighlight(highlight) {
    if (this.readOnlyValue) return false
    const records = await removeHighlightRecord({ highlight, records: this.highlightRecords, request: requestJson })
    if (!records) return false
    this.rendition.annotations.remove(highlight.location.cfi, "highlight")
    this.highlightRecords = records
    const survivor = records.findLast((record) => record.location.cfi === highlight.location.cfi)
    if (survivor) this.applyHighlight(survivor)
    this.renderHighlights()
    return true
  }

  async removeBookmark(bookmark) {
    await this.removeBookmarks([bookmark])
  }

  async removeBookmarks(bookmarks) {
    if (this.readOnlyValue) return
    this.bookmarkRecords = await removeBookmarkRecords({ bookmarks, records: this.bookmarkRecords, request: requestJson })
    this.renderBookmarks()
    this.syncBookmarkButton()
  }

}
